import type { DragEvent } from "react"
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react"

import type { ReportTemplateFieldDto } from "../../api/reportTemplateService"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"

export interface FieldsTabProps {
  fields: ReportTemplateFieldDto[]
  dragFieldIndex: number | null
  dragOverFieldIndex: number | null
  onAddField: () => void
  onEditField: (index: number) => void
  onDeleteField: (index: number) => void
  onFieldDragStart: (index: number) => void
  onFieldDragOver: (index: number, event: DragEvent<HTMLTableRowElement>) => void
  onFieldDrop: (index: number) => void
  onFieldDragEnd: () => void
}

export function FieldsTab({
  fields,
  dragFieldIndex,
  dragOverFieldIndex,
  onAddField,
  onEditField,
  onDeleteField,
  onFieldDragStart,
  onFieldDragOver,
  onFieldDrop,
  onFieldDragEnd,
}: FieldsTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500">Drag and drop each row to reorder fields.</p>
        <Button onClick={onAddField} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Field
        </Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[52px]"></TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Field Key</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Source Path</TableHead>
              <TableHead>Flags</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-gray-500">
                  No fields configured
                </TableCell>
              </TableRow>
            ) : (
              fields.map((item, index) => (
                <TableRow
                  key={`${item.fieldKey}-${index}`}
                  draggable
                  onDragStart={() => onFieldDragStart(index)}
                  onDragOver={(event) => onFieldDragOver(index, event)}
                  onDrop={() => onFieldDrop(index)}
                  onDragEnd={onFieldDragEnd}
                  className={[
                    "cursor-move",
                    dragFieldIndex === index ? "opacity-60" : "",
                    dragOverFieldIndex === index && dragFieldIndex !== index ? "bg-blue-50/70" : "",
                  ]
                    .join(" ")
                    .trim()}
                >
                  <TableCell>
                    <div className="flex items-center justify-center text-gray-400">
                      <GripVertical className="h-4 w-4" />
                    </div>
                  </TableCell>
                  <TableCell>{item.orderIndex}</TableCell>
                  <TableCell className="font-medium">{item.fieldKey}</TableCell>
                  <TableCell>{item.label}</TableCell>
                  <TableCell>{item.dataType}</TableCell>
                  <TableCell>{item.sourcePath || "-"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {item.defaultVisible ? <Badge variant="outline">visible</Badge> : null}
                      {item.isFilterable ? <Badge variant="outline">filter</Badge> : null}
                      {item.isSortable ? <Badge variant="outline">sort</Badge> : null}
                      {item.isGroupable ? <Badge variant="outline">group</Badge> : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => onEditField(index)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onDeleteField(index)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
